/*
 * collection-m2-sort
 *
 * Sorts collection pages AND the search results page by the price the customer
 * actually sees.
 *
 * Shopify sorts server-side on variant.price. For products sold by the box that
 * field holds the price of a box, while the card shows that divided by the box
 * coverage. Coverage varies (1,08 / 1,23 / 1,44 ...), so the two orders
 * disagree: a 495 kr/m² tile in a 0,72 m² box costs 356 kr and sorts ahead of a
 * 349 kr/m² tile in a 1,44 m² box at 503 kr.
 *
 * Nothing in Shopify can sort on the computed figure, so the ordering is done
 * here instead:
 *
 *   1. The two native price options are removed from the sort dropdown, since
 *      they return an order that contradicts the prices on the cards.
 *   2. Two price options are added in their place.
 *   3. Choosing one fetches every page of ?view=sortdata for the current page
 *      AND the current filters, sorts on the computed price, and renders the
 *      results.
 *
 * The same file drives both pages. The only real differences are where the data
 * comes from (collection.sortdata vs search.sortdata) and the fact that a
 * search can return products that were never sold by m². Those sort on their
 * ordinary price, which is what the card shows, so a mixed result set still
 * reads correctly top to bottom.
 *
 * The data request carries the active filter parameters, and on search also the
 * query and type, because the sorted set has to match what the customer is
 * actually looking at. Results are cached per page and filter combination, so
 * switching between ascending and descending, or paging through, costs nothing
 * after the first load.
 */
(function () {
  var PARAM = 'm2sort';
  var PAGE_PARAM = 'm2page';
  var NATIVE_BROKEN = ['price-ascending', 'price-descending'];

  /*
   * Fallbacks only. The two price labels are lifted from the theme's own sort
   * options before those are removed, so they arrive already translated and a
   * Danish or English storefront reads correctly without touching this file.
   * The previous/next wording is likewise harvested from the rendered
   * pagination. These values are used only if either harvest fails.
   *
   * Deliberately neutral wording: the sort key is the price printed on the card,
   * which is kr/m² on tiles and a plain price on everything else, so naming it
   * "per m²" would be wrong on a search containing bathroom furniture.
   */
  var LABELS = {
    asc: 'Pris, lågt till högt',
    desc: 'Pris, högt till lågt',
    previous: 'Föregående',
    next: 'Nästa',
    page: 'Sida',
  };

  var cache = {};
  var state = { direction: null, products: null, page: 1, pageSize: 20 };

  /* ------------------------------------------------------------------ *
   * Where are we
   * ------------------------------------------------------------------ */

  function collectionPath() {
    var match = window.location.pathname.match(/^(\/[a-z-]{2,5})?(\/collections\/[^/]+)/);
    return match ? (match[1] || '') + match[2] : null;
  }

  function searchPath() {
    var match = window.location.pathname.match(/^(\/[a-z-]{2,5})?\/search\/?$/);
    return match ? window.location.pathname.replace(/\/$/, '') : null;
  }

  /*
   * The Sidor tab has no prices and no sort dropdown, so there is nothing to do
   * there. Checked explicitly rather than relying on the dropdown being absent,
   * so a future layout change cannot quietly turn this on.
   */
  function isPagesTab() {
    var type = new URLSearchParams(window.location.search).get('type') || '';
    if (type.indexOf('product') !== -1) return false;
    return type.indexOf('article') !== -1 || type.indexOf('page') !== -1;
  }

  function dataPath() {
    var collection = collectionPath();
    if (collection) return collection;
    if (searchPath() && !isPagesTab()) return searchPath();
    return null;
  }

  /* ------------------------------------------------------------------ *
   * Helpers
   * ------------------------------------------------------------------ */

  /*
   * Everything except paging and our own parameters. Filters must be carried
   * through or the sorted set would not match what the customer filtered to,
   * and on search the query and type ride along for the same reason.
   */
  function filterParams() {
    var params = new URLSearchParams(window.location.search);
    params.delete('page');
    params.delete('sort_by');
    params.delete(PARAM);
    params.delete(PAGE_PARAM);
    return params;
  }

  function cacheKey() {
    return dataPath() + '?' + filterParams().toString();
  }

  function grid() {
    return document.getElementById('ProductsList');
  }

  function customDirection() {
    var direction = new URLSearchParams(window.location.search).get(PARAM);
    return direction === 'asc' || direction === 'desc' ? direction : null;
  }

  /*
   * FacetForm rebuilds its request from FormData rather than from the current
   * URL. Keep collection forms aligned with an active custom sort so changing
   * filters cannot resurrect a native sort value left in their hidden inputs.
   */
  function syncCollectionFacetForms() {
    if (!collectionPath()) return;

    var direction = customDirection();
    var params = new URLSearchParams(window.location.search);
    params.delete('sort_by');
    params.delete('page');
    params.delete(PAGE_PARAM);

    Array.prototype.slice
      .call(document.querySelectorAll('form[is="facet-form"]'))
      .forEach(function (form) {
        Array.prototype.slice
          .call(form.querySelectorAll('[data-m2-sort-param]'))
          .forEach(function (input) {
            input.remove();
          });

        if (!direction) return;

        Array.prototype.slice
          .call(form.querySelectorAll('input[name="sort_by"]'))
          .forEach(function (input) {
            input.remove();
          });

        var formData = new FormData(form);
        params.set(PARAM, direction);
        params.forEach(function (value, name) {
          if (formData.has(name)) return;

          var input = document.createElement('input');
          input.type = 'hidden';
          input.name = name;
          input.value = value;
          input.setAttribute('data-m2-sort-param', 'true');
          form.appendChild(input);
        });
      });
  }

  /*
   * Filter-removal chips bypass FormData and send their href straight to the
   * facet renderer. Carry the custom sort and unrelated current parameters into
   * those collection URLs while leaving the link's remaining filters intact.
   */
  function preserveCustomSortOnFacetLink(event) {
    if (!collectionPath()) return;

    var direction = customDirection();
    var target = event.target;
    var link = target && target.closest ? target.closest('a[is="facet-remove"]') : null;
    if (!direction || !link) return;

    var current = new URL(window.location.href);
    var url = new URL(link.href, current.origin);
    var copied = {};

    current.searchParams.forEach(function (value, name) {
      if (
        name.indexOf('filter.') === 0 ||
        name === PARAM ||
        name === PAGE_PARAM ||
        name === 'page' ||
        name === 'sort_by' ||
        name === 'section_id'
      ) {
        return;
      }

      if (!copied[name]) {
        url.searchParams.delete(name);
        copied[name] = true;
      }
      url.searchParams.append(name, value);
    });

    url.searchParams.delete('sort_by');
    url.searchParams.delete('page');
    url.searchParams.delete(PAGE_PARAM);
    url.searchParams.set(PARAM, direction);
    link.href = url.toString();
  }

  /*
   * The two sections wrap their cards in different classes: product-column on
   * collections, card on search. Cloned from whatever the page already
   * rendered rather than hardcoded, so the sorted grid keeps the same layout.
   */
  function columnClass() {
    var list = grid();
    var sample = list ? list.querySelector('.f-column') : null;
    return sample ? sample.className : 'f-column product-column';
  }

  /* ------------------------------------------------------------------ *
   * Data
   * ------------------------------------------------------------------ */

  function fetchPage(page) {
    var params = filterParams();
    params.set('view', 'sortdata');
    if (page > 1) params.set('page', String(page));

    return fetch(dataPath() + '?' + params.toString(), {
      headers: { Accept: 'text/html' },
    })
      .then(function (response) {
        if (!response.ok) throw new Error('sortdata request failed: ' + response.status);
        return response.text();
      })
      .then(function (text) {
        return JSON.parse(text);
      });
  }

  /*
   * Page one is fetched first because it reports how many pages there are; the
   * rest go out together rather than in sequence.
   */
  function fetchAll() {
    var key = cacheKey();
    if (cache[key]) return Promise.resolve(cache[key]);

    return fetchPage(1).then(function (first) {
      var rest = [];
      for (var page = 2; page <= first.pages; page++) {
        rest.push(fetchPage(page));
      }

      return Promise.all(rest).then(function (pages) {
        var products = first.products.slice();
        pages.forEach(function (payload) {
          products = products.concat(payload.products);
        });
        cache[key] = products;
        return products;
      });
    });
  }

  /* ------------------------------------------------------------------ *
   * Rendering
   * ------------------------------------------------------------------ */

  /*
   * Prefer the complete card rendered by the theme's card-product snippet. This
   * keeps badges, secondary images, swatches and card actions in step with the
   * native collection/search markup. The smaller builder remains as a defensive
   * fallback if a stale sortdata response does not contain card_html yet.
   */
  function buildCard(product) {
    var column = document.createElement('div');
    column.className = columnClass();

    if (product.card_html) {
      var template = document.createElement('template');
      template.innerHTML = product.card_html.trim();

      var renderedCard = template.content.firstElementChild;
      if (renderedCard) {
        column.appendChild(renderedCard);
        return column;
      }
    }

    var card = document.createElement('div');
    card.className = 'product-card product-card-style-' + (window.__m2CardStyle || 'standard');

    var wrapper = document.createElement('div');
    wrapper.className = 'product-card__wrapper h-full';

    var imageWrap = document.createElement('div');
    imageWrap.className = 'product-card__image-wrapper product-card__image-wrapper--main-only';

    var imageLink = document.createElement('a');
    imageLink.href = product.url;
    imageLink.setAttribute('aria-label', product.title);
    imageLink.tabIndex = -1;

    if (product.image) {
      var figure = document.createElement('div');
      figure.className = 'media-wrapper product-card__image product-card__image--main';
      figure.style.setProperty('--aspect-ratio', '1');

      var img = document.createElement('img');
      img.src = product.image;
      img.alt = product.image_alt || product.title;
      img.loading = 'lazy';
      figure.appendChild(img);
      imageLink.appendChild(figure);
    }

    imageWrap.appendChild(imageLink);
    wrapper.appendChild(imageWrap);

    var info = document.createElement('div');
    info.className = 'product-card__info';

    var heading = document.createElement('h3');
    heading.className = 'product-card__title text-pcard-title';
    var titleLink = document.createElement('a');
    titleLink.className = 'reversed-link block';
    titleLink.href = product.url;
    var titleText = document.createElement('span');
    titleText.className = 'reversed-link__text';
    titleText.textContent = product.title;
    titleLink.appendChild(titleText);
    heading.appendChild(titleLink);
    info.appendChild(heading);

    if (product.price_html) {
      var price = document.createElement('div');
      price.innerHTML = product.price_html;
      while (price.firstChild) info.appendChild(price.firstChild);
    }

    wrapper.appendChild(info);
    card.appendChild(wrapper);
    column.appendChild(card);
    return column;
  }

  /*
   * Page size is taken from the grid the section already rendered, so the
   * sorted view breaks into the same number of products per page as the rest of
   * the store rather than a number hardcoded here.
   */
  function detectPageSize() {
    var list = grid();
    if (!list) return 20;
    var count = list.querySelectorAll('.f-column').length;
    return count >= 8 ? count : 20;
  }

  function totalPages() {
    if (!state.products) return 1;
    return Math.max(1, Math.ceil(state.products.length / state.pageSize));
  }

  function renderPage() {
    var list = grid();
    if (!list || !state.products) return;

    var start = (state.page - 1) * state.pageSize;
    var slice = state.products.slice(start, start + state.pageSize);

    list.innerHTML = '';
    slice.forEach(function (product) {
      list.appendChild(buildCard(product));
    });

    renderPagination();
  }

  function goToPage(page) {
    state.page = Math.min(Math.max(1, page), totalPages());

    var url = new URL(window.location.href);
    if (state.page > 1) {
      url.searchParams.set(PAGE_PARAM, String(state.page));
    } else {
      url.searchParams.delete(PAGE_PARAM);
    }
    window.history.replaceState({}, '', url.toString());

    renderPage();

    var list = grid();
    if (list) {
      var top = list.getBoundingClientRect().top + window.pageYOffset - 120;
      window.scrollTo({ top: top, behavior: 'smooth' });
    }
  }

  /* ------------------------------------------------------------------ *
   * Pagination
   *
   * Rebuilt with the same markup snippets/pagination.liquid produces for the
   * "number" style, so the sorted view looks identical to every other
   * collection or search page.
   * ------------------------------------------------------------------ */

  /*
   * The caret icons and the previous / next wording are lifted from the
   * pagination the section rendered, rather than reproduced here, so they stay
   * correct if the theme or the translations change.
   */
  function harvestChrome() {
    if (state.chrome) return state.chrome;

    var source = document.querySelector('.pagination:not([data-m2-pagination])');
    var chrome = { prevIcon: null, nextIcon: null, prev: LABELS.previous, next: LABELS.next };

    if (source) {
      var prev = source.querySelector('.pagination__item--previous');
      var next = source.querySelector('.pagination__item--next');

      if (prev) {
        var prevSvg = prev.querySelector('svg');
        if (prevSvg) chrome.prevIcon = prevSvg.cloneNode(true);
        var prevText = prev.querySelector('span');
        if (prevText) chrome.prev = prevText.textContent.trim();
      }
      if (next) {
        var nextSvg = next.querySelector('svg');
        if (nextSvg) chrome.nextIcon = nextSvg.cloneNode(true);
        var nextText = next.querySelector('span');
        if (nextText) chrome.next = nextText.textContent.trim();
      }
    }

    state.chrome = chrome;
    return chrome;
  }

  /*
   * Same windowing Shopify applies: first and last page always shown, a couple
   * either side of the current one, and a gap marker for the rest.
   */
  function pageParts() {
    var total = totalPages();
    var current = state.page;
    var parts = [];
    var wanted = {};

    wanted[1] = true;
    wanted[total] = true;
    for (var offset = -2; offset <= 2; offset++) {
      var page = current + offset;
      if (page >= 1 && page <= total) wanted[page] = true;
    }

    var previous = 0;
    Object.keys(wanted)
      .map(Number)
      .sort(function (a, b) {
        return a - b;
      })
      .forEach(function (page) {
        if (previous && page - previous > 1) parts.push(null);
        parts.push(page);
        previous = page;
      });

    return parts;
  }

  function pageLink(page, label, extraClass, icon, iconFirst) {
    var link = document.createElement('a');
    link.href = '#';
    link.className = 'pagination__item' + (extraClass ? ' ' + extraClass : '');
    link.setAttribute('aria-label', LABELS.page + ' ' + page);

    var text = document.createElement('span');
    text.textContent = label;

    if (icon && iconFirst) link.appendChild(icon);
    link.appendChild(icon ? text : document.createTextNode(label));
    if (icon && !iconFirst) link.appendChild(icon);

    link.addEventListener('click', function (event) {
      event.preventDefault();
      goToPage(page);
    });

    return link;
  }

  function renderPagination() {
    var existing = document.querySelector('[data-m2-pagination]');
    if (existing) existing.remove();

    var total = totalPages();
    if (total <= 1) return;

    var chrome = harvestChrome();

    var nav = document.createElement('nav');
    nav.className = 'pagination';
    nav.setAttribute('role', 'navigation');
    nav.setAttribute('data-m2-pagination', 'true');

    var list = document.createElement('ul');
    list.className = 'pagination__list list-unstyled flex items-center flex-wrap justify-center';
    list.setAttribute('role', 'list');

    function add(node) {
      var item = document.createElement('li');
      item.appendChild(node);
      list.appendChild(item);
    }

    if (state.page > 1) {
      add(
        pageLink(
          state.page - 1,
          chrome.prev,
          'pagination__item--previous pagination__item-arrow motion-reduce',
          chrome.prevIcon ? chrome.prevIcon.cloneNode(true) : null,
          true
        )
      );
    }

    pageParts().forEach(function (page) {
      if (page === null) {
        var gap = document.createElement('span');
        gap.className = 'pagination__item';
        gap.textContent = '…';
        add(gap);
        return;
      }

      if (page === state.page) {
        var current = document.createElement('a');
        current.setAttribute('role', 'link');
        current.setAttribute('aria-disabled', 'true');
        current.setAttribute('aria-current', 'page');
        current.setAttribute('aria-label', LABELS.page + ' ' + page);
        current.className = 'pagination__item pagination__item--current background-2';
        current.textContent = String(page);
        add(current);
        return;
      }

      add(pageLink(page, String(page), '', null, false));
    });

    if (state.page < total) {
      add(
        pageLink(
          state.page + 1,
          chrome.next,
          'pagination__item--next pagination__item-arrow motion-reduce',
          chrome.nextIcon ? chrome.nextIcon.cloneNode(true) : null,
          false
        )
      );
    }

    nav.appendChild(list);
    grid().parentNode.appendChild(nav);
  }

  function setBusy(isBusy) {
    var list = grid();
    if (!list) return;
    list.style.opacity = isBusy ? '0.4' : '';
    list.setAttribute('aria-busy', isBusy ? 'true' : 'false');
  }

  /* ------------------------------------------------------------------ *
   * Sorting
   * ------------------------------------------------------------------ */

  function activate(direction) {
    var list = grid();
    if (!list || !dataPath()) return;

    state.direction = direction;
    if (!state.products) state.pageSize = detectPageSize();
    setBusy(true);

    fetchAll()
      .then(function (products) {
        var sorted = products.slice().sort(function (a, b) {
          if (a.sort_price === b.sort_price) {
            return a.title.localeCompare(b.title, 'sv');
          }
          return direction === 'desc'
            ? b.sort_price - a.sort_price
            : a.sort_price - b.sort_price;
        });

        state.products = sorted;

        /*
         * Read the chrome before the section's own pagination is hidden, since
         * the icons and wording are borrowed from it.
         */
        harvestChrome();

        var themePagination = document.querySelector('.pagination:not([data-m2-pagination])');
        if (themePagination) themePagination.style.display = 'none';

        var requestedPage = parseInt(
          new URLSearchParams(window.location.search).get(PAGE_PARAM) || '1',
          10
        );
        state.page = Math.min(Math.max(1, requestedPage || 1), totalPages());

        renderPage();
      })
      .catch(function (error) {
        console.error('m² sort failed, leaving the page as Shopify returned it:', error);
      })
      .finally(function () {
        setBusy(false);
      });
  }

  /* ------------------------------------------------------------------ *
   * Sort dropdown
   * ------------------------------------------------------------------ */

  function decorate(select) {
    if (!select || select.dataset.m2Decorated === 'true') return;
    select.dataset.m2Decorated = 'true';

    // Take the theme's own translated wording before discarding the options.
    NATIVE_BROKEN.forEach(function (value, index) {
      var option = select.querySelector('option[value="' + value + '"]');
      if (!option) return;

      var text = (option.textContent || '').trim();
      if (text) LABELS[index === 0 ? 'asc' : 'desc'] = text;

      option.remove();
    });

    ['asc', 'desc'].forEach(function (direction) {
      var value = 'm2-' + direction;
      if (select.querySelector('option[value="' + value + '"]')) return;
      var option = document.createElement('option');
      option.value = value;
      option.textContent = LABELS[direction];
      select.appendChild(option);
    });

    var current = new URLSearchParams(window.location.search).get(PARAM);
    if (current) select.value = 'm2-' + current;

    /*
     * Capture phase, so the choice is intercepted before the theme's facet code
     * tries to send an unknown sort_by to Shopify.
     */
    select.addEventListener(
      'change',
      function (event) {
        var value = select.value;
        if (value.indexOf('m2-') !== 0) {
          /*
           * On collections, hand native options a clean URL before the theme's
           * own change handler reads it. Search keeps its existing behaviour.
           */
          if (collectionPath()) {
            state.direction = null;
            state.products = null;

            var nativeUrl = new URL(window.location.href);
            nativeUrl.searchParams.delete(PARAM);
            nativeUrl.searchParams.delete(PAGE_PARAM);
            nativeUrl.searchParams.delete('page');
            window.history.replaceState({}, '', nativeUrl.toString());
            syncCollectionFacetForms();
          }
          return;
        }

        event.stopPropagation();
        event.preventDefault();

        var direction = value.slice(3);
        state.page = 1;

        var url = new URL(window.location.href);
        url.searchParams.delete('sort_by');
        url.searchParams.delete('page');
        url.searchParams.delete(PAGE_PARAM);
        url.searchParams.set(PARAM, direction);
        window.history.replaceState({}, '', url.toString());
        syncCollectionFacetForms();

        activate(direction);
      },
      true
    );
  }

  function decorateAll(root) {
    var scope = root && root.querySelectorAll ? root : document;
    Array.prototype.slice
      .call(scope.querySelectorAll('select[name="sort_by"]'))
      .forEach(decorate);

    syncCollectionFacetForms();
  }

  /* ------------------------------------------------------------------ *
   * Boot
   * ------------------------------------------------------------------ */

  function init() {
    if (!dataPath() || !grid()) return;

    var params = new URLSearchParams(window.location.search);

    /*
     * Repair previously generated collection URLs containing both systems.
     * sort_by represents the native option the customer selected most recently,
     * so it wins over the stale custom parameter in that invalid state.
     */
    if (collectionPath() && params.has('sort_by') && params.has(PARAM)) {
      var url = new URL(window.location.href);
      url.searchParams.delete(PARAM);
      url.searchParams.delete(PAGE_PARAM);
      window.history.replaceState({}, '', url.toString());
      params = new URLSearchParams(window.location.search);
    }

    decorateAll(document);

    var requested = params.get(PARAM);
    if (requested === 'asc' || requested === 'desc') {
      activate(requested);
    }
  }

  document.addEventListener('DOMContentLoaded', init);

  /*
   * Filtering re-renders the toolbar and the grid. The dropdown has to be
   * decorated again, and an active sort has to be reapplied to the newly
   * filtered set.
   */
  if (window.MutationObserver) {
    var observer = new MutationObserver(function (mutations) {
      if (!dataPath()) return;

      var sawSelect = false;
      var sawGrid = false;

      mutations.forEach(function (mutation) {
        Array.prototype.slice.call(mutation.addedNodes).forEach(function (node) {
          if (node.nodeType !== 1) return;
          if (node.matches && node.matches('select[name="sort_by"]')) sawSelect = true;
          if (node.querySelector && node.querySelector('select[name="sort_by"]')) sawSelect = true;
          if (node.id === 'ProductsList' || (node.querySelector && node.querySelector('#ProductsList'))) {
            sawGrid = true;
          }
        });
      });

      if (sawSelect) decorateAll(document);
      if (sawGrid && collectionPath()) {
        state.direction = customDirection();
        if (state.direction) {
          activate(state.direction);
        } else {
          state.products = null;
        }
      } else if (sawGrid && state.direction) {
        activate(state.direction);
      }
    });

    observer.observe(document.documentElement, { childList: true, subtree: true });
  }

  document.addEventListener('click', preserveCustomSortOnFacetLink, true);
})();
